import React, { useEffect } from "react";
import { MAC_DOWNLOAD_URL, markHasDesktopApp } from "../components/DesktopInstallBanner";
import "./DownloadPage.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

// Public, permanently reachable download page (/download), styled like an
// app-store listing (founder direction: "it's going to feel like an
// app, so might as well treat it like one"; final look locked same day —
// listing card + info row only, NO screenshot rail, Mac above / Windows
// below). Everything on it is honest — no fabricated ratings or reviews.
//
// Why the page exists: the install banner hides for 7 days once dismissed and
// the install email is one-shot — this is the always-reachable (and Google-
// indexable) home for the Mac download. Linked from the site footer.

// Same Apple mark the AppleButton uses; inherits text color.
const AppleMark: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.51-3.23 0-1.44.64-2.2.45-3.06-.4C3.79 16.17 4.36 9.02 8.82 8.78c1.15.06 1.95.65 2.63.68.99-.2 1.95-.78 3.01-.7 1.28.1 2.24.61 2.87 1.56-2.64 1.57-2.01 5.02.36 5.98-.49 1.3-1.13 2.58-2.64 3.98zM12.03 8.7c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

// Who is looking. A Windows computer gets its steps FIRST (the Mac app is no
// use there); a phone or tablet is told this page is for a computer (the
// talent tips email's "Download Now" is mostly tapped on a phone). Everyone
// else keeps the locked look: Mac above, Windows below.
export function downloadViewer(
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  touchPoints: number = typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0
): "windows" | "phone" | "other" {
  if (/iPhone|iPod|iPad|Android/i.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1)) return "phone";
  if (/Windows/i.test(ua)) return "windows";
  return "other";
}

const DownloadPage: React.FC = () => {
  const viewer = downloadViewer();
  useEffect(() => {
    document.title = t("downloadPage.downloadRequestAppFor");
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content") ?? null;
    meta?.setAttribute(
      "content",
      t("downloadPage.downloadTheFreeRequest")
    );
    // Structured data: declare the official macOS app to Google by name —
    // "Request App" — with the real download URL. Mounted only on /download.
    // Skipped when api/static-meta already baked the same block into the HTML.
    if (document.getElementById("ld-static-page")) {
      return () => {
        if (meta && prev !== null) meta.setAttribute("content", prev);
      };
    }
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Request App",
      operatingSystem: "macOS 11.0 or later",
      applicationCategory: "MusicApplication",
      downloadUrl: MAC_DOWNLOAD_URL,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Requesta LLC" },
    });
    document.head.appendChild(ld);
    return () => {
      if (meta && prev !== null) meta.setAttribute("content", prev);
      ld.remove();
    };
  }, []);

  return (
    <main className="download-page" role="main">
      <header className="download-hero">
        {/* The desktop tip email's picture (talent tips #4): a request pops up over the DJ software. */}
        <img className="download-anim" src="/email/tips/tip-desktop.gif" alt="" width={900} height={450} />
        <p className="download-eyebrow">{t("downloadPage.download")}</p>
        <h1 className="download-title">{t("downloadPage.requestAppForYour")}</h1>
        <p className="download-tagline">
          {t("downloadPage.theDesktopAppFloats")}
        </p>
        {viewer === "phone" && <p className="download-phone-note">{t("downloadPage.openThisPageOn")}</p>}
      </header>

      {viewer === "windows" && windowsSteps(true)}

      <section className="store-card" aria-label={t("downloadPage.requestAppForMac")}>
        <img
          className="store-icon"
          src="/appicon-1024.png"
          alt={t("downloadPage.requestAppIcon")}
          width={110}
          height={110}
        />
        <div className="store-card-text">
          <p className="store-eyebrow">
            <AppleMark size={13} />{t("downloadPage.macApp")}
          </p>
          <h2 className="store-name">{t("downloadPage.requestApp")}</h2>
          <p className="store-subtitle">{t("downloadPage.getPaidForSong")}</p>
        </div>
        <a href={MAC_DOWNLOAD_URL} download className="store-get" aria-label={t("downloadPage.downloadRequestAppFor")} onClick={markHasDesktopApp}>
          {t("downloadPage.download")}
        </a>
      </section>

      <section className="store-info" aria-label={t("downloadPage.appInformation")}>
        <div className="store-info-cell">
          <span className="store-info-label">{t("downloadPage.price")}</span>
          <span className="store-info-value">{t("downloadPage.free")}</span>
        </div>
        <div className="store-info-cell">
          <span className="store-info-label">{t("downloadPage.security")}</span>
          <span className="store-info-value">{t("downloadPage.notarizedByApple")}</span>
        </div>
        <div className="store-info-cell">
          <span className="store-info-label">{t("downloadPage.requires")}</span>
          <span className="store-info-value">{t("downloadPage.macos")}</span>
        </div>
        <div className="store-info-cell">
          <span className="store-info-label">{t("downloadPage.chip")}</span>
          <span className="store-info-value">{t("downloadPage.appleSiliconIntel")}</span>
        </div>
      </section>

      {viewer !== "windows" && windowsSteps(false)}
    </main>
  );
};

const windowsSteps = (first: boolean) => (
  <section className={first ? "download-windows download-windows--first" : "download-windows"} aria-label={t("downloadPage.windowsInstructions")}>
    <h2 className="download-windows-title">{t("downloadPage.onWindows")}</h2>
    <p className="download-windows-text">
      <T k="downloadPage.openRequestaAppIn" tags={{ 0: (c) => <strong>{c}</strong>, 1: (c) => <strong>{c}</strong> }} />
    </p>
  </section>
);

export default DownloadPage;
